import BaseService from './base.service'
import { useAuthStore } from '@/stores'
import type { NewPasswordProps } from '@/types/types'

import { useRouter } from 'vue-router'
import baseApi from './base.api'


export const authService = {
  ...baseApi,
  resource: 'autenticacao',

  async bulkforgotPassword(emailList: string[]) {
    return await baseApi.post(`${this.resource}/ForgotPassword/Batch`, {
      emails: emailList
    })
  }
}

class AutenticacaoService extends BaseService {
  formData: {}
  constructor() {
    super({ resource: 'autenticacao' })

    const { scope } = (<any>window).configuration?.aliare?.credentials || {}

    this.formData = {
      scope
    }
  }

  async forgotPassword(email: string, tenantId?: string, recaptchaToken?: string) {
    let headers = {}

    if (!tenantId && !useAuthStore().loggedIn) {
      const tenantId = await useAuthStore().getActiveTentant
      headers = tenantId && tenantId !== (window as any)?.configuration?.plantar?.tenant ? { 'x-tenant': tenantId } : {}
    } else if (tenantId) {
      headers = { 'x-tenant': tenantId }
    }

    // Em header: query string fica gravada no log de acesso do gateway.
    if (recaptchaToken) headers = { ...headers, recaptchaToken }

    return await this.plantarApi.get(`${this.resource}/forgotpassword/${email}`, { headers })
  }

  async registerLogAuthentication() {
    try {
      return await this.plantarApi.post('AuthenticationLog') 
    } catch (error) {
      //
    }

    return { success: false }
  }

  async silentRefresh(refreshToken) {
    const payload = {
      ...this.formData,
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    }

    try {
      const { data } = await this.plantarApi.post(`${this.resource}/Refresh`, payload)

      if (!data || !data.access_Token) {
        throw new Error('Token de acesso inválido')
      }

      return {
        access_token: data.access_Token,
        expires_in: data.expires_In,
        refresh_token: data.refresh_Token,
        scope: data.scope,
        token_type: data.token_Type,
        updatePassword: data.updatePassword,
        // Identidade do usuário (sub, IdDoUsuario, unique_name, email, Tenant, Level). O Aliare Auth
        // não emite mais essas claims no token; a API as resolve no /connect/userinfo e devolve aqui.
        user: data.user
      }
    } catch (error) {
      useRouter().push('/logout')

      throw new Error('Falha ao atualizar o token de acesso. Por favor, faça login novamente.')
    }
  }

  async login({ username, password, recaptchaToken }: { username: string, password: string, recaptchaToken?: string }) {
    const payload = {
      username: username.replace(/\s/g,''),
      password: password,
      ...(recaptchaToken ? { recaptchaToken } : {})
    }
  
    try {
      const { data } = await this.plantarApi.post(this.resource, payload);
  
      const formattedData = {
        access_token: data.access_Token,
        expires_in: data.expires_In,
        refresh_token: data.refresh_Token,
        scope: data.scope,
        token_type: data.token_Type,
        updatePassword: data.updatePassword,
        // Identidade do usuário (sub, IdDoUsuario, unique_name, email, Tenant, Level). O Aliare Auth
        // não emite mais essas claims no token; a API as resolve no /connect/userinfo e devolve aqui.
        user: data.user,
        // Desafio de segundo fator. Quando presente, os campos acima vêm todos nulos: o token
        // existe, mas fica retido na API até o código ser validado.
        twoFactor: data.twoFactor ?? null
      }
  
      return formattedData
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Conclui um login que exigiu segundo fator. A resposta tem exatamente o mesmo formato do login,
   * então o tratamento a partir daqui é o mesmo.
   */
  async verifyTwoFactor({ ticket, code }: { ticket: string, code: string }) {
    const { data } = await this.plantarApi.post(`${this.resource}/TwoFactor/Verify`, { ticket, code })

    return {
      access_token: data.access_Token,
      expires_in: data.expires_In,
      refresh_token: data.refresh_Token,
      scope: data.scope,
      token_type: data.token_Type,
      updatePassword: data.updatePassword,
      user: data.user
    }
  }

  /**
   * Pede outro código para o mesmo desafio. Pedir antes do intervalo mínimo não é erro: a API
   * responde 200 sem enviar nada e `resendInSeconds` diz quanto falta.
   */
  async resendTwoFactor(ticket: string) {
    const { data } = await this.plantarApi.post(`${this.resource}/TwoFactor/Resend`, { ticket })

    return data
  }

  async sendNewPassword(params: NewPasswordProps) {
    try {
      const { data, success } = await this.plantarApi.post(`${this.resource}/CreatePassword`, params)
      return {
        success,
        data
      }
    } catch (error) {
      return {
        success: false,
        data: null
      }
    }
  }
}

const autenticacaoService = new AutenticacaoService()

export default autenticacaoService
